/**
 * AI Intelligence Panel Component (Phase 7.8)
 * 
 * Displays cross-market intelligence and emerging opportunities from AI advisory.
 */

import { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import { Psychology, Lightbulb } from '@mui/icons-material';
import { aiIntelligenceApi } from '../services/api';
import type { AIIntelligence } from '../types';

export function AIIntelligencePanel() {
  const [intelligence, setIntelligence] = useState<AIIntelligence | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadIntelligence();
  }, []);

  const loadIntelligence = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await aiIntelligenceApi.getIntelligence();
      setIntelligence(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AI intelligence');
      console.error('AI Intelligence load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!intelligence || intelligence.status === 'unavailable') {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            AI Intelligence
          </Typography>
          <Alert severity="info">
            AI intelligence is currently unavailable. This may be because AI advisory has not been generated yet or is disabled.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (intelligence.status === 'error') {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            AI Intelligence
          </Typography>
          <Alert severity="warning">
            Failed to load AI intelligence. Please try again later.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">AI Intelligence</Typography>
          <Box display="flex" gap={1} alignItems="center">
            {intelligence.model && (
              <Chip
                label={intelligence.model}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
            {intelligence.lastUpdatedAt && (
              <Typography variant="caption" color="text.secondary">
                {new Date(intelligence.lastUpdatedAt).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {/* Cross-Market Intelligence */}
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Psychology color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight="bold">
                Cross-Market Intelligence
              </Typography>
            </Box>
            {intelligence.crossMarketIntelligence ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  maxHeight: '300px',
                  overflow: 'auto',
                }}
              >
                {intelligence.crossMarketIntelligence}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No cross-market intelligence available for this snapshot.
              </Typography>
            )}
          </Box>

          {/* Emerging Opportunities */}
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Lightbulb color="warning" fontSize="small" />
              <Typography variant="subtitle1" fontWeight="bold">
                Emerging Opportunities
              </Typography>
            </Box>
            {intelligence.emergingOpportunities && intelligence.emergingOpportunities.length > 0 ? (
              <List dense sx={{ maxHeight: '300px', overflow: 'auto' }}>
                {intelligence.emergingOpportunities.map((opp, index) => (
                  <Box key={index}>
                    <ListItem disablePadding>
                      <ListItemText
                        primary={
                          <Typography variant="body2" color="text.secondary">
                            {opp}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {index < intelligence.emergingOpportunities!.length - 1 && <Divider />}
                  </Box>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                No emerging opportunities identified at this time.
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

